import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '#app/hooks/useAppStore';
import { handleCloseModal, register, requestRegister } from '../../actions/auth';
import { Container, GreyText, Text, TextTimer, Wrapper } from './styles';
import { Button, Input, InputContainer } from 'finform';
import { Row } from '#app/pages/styles';
import { NotifyManager } from 'notify-component';
import { Caption } from '#app/components/Text/styles';
import LocalStorageHelper from '#app/helpers/Cache/LocalStorageHelper';
import { useRegisterForm } from './hooks/useRegisterForm';
import {
  shouldShowFieldError,
  getFieldErrorFromMeta,
} from '#app/utils/tanstackFormErrorVisibility';

export interface RegisterI18n {
  auth: {
    codeAdvise: string;
    code: string;
    resendCode: string;
    resendCodeButton: string;
    issue: string;
    confirm: string;
    email?: string;
    password: string;
    passwordRepeat: string;
    promoCode: string;
    register?: string;
    toRegister: string;
  };
}

export interface RegisterProps {
  callback?: () => void;
}

function Register({ callback }: RegisterProps = {}) {
  const dispatch = useAppDispatch();
  const i18n = useAppSelector((s) => s.i18n) as any;
  const handleCloseModalFn = useCallback(() => dispatch(handleCloseModal() as any), [dispatch]);
  const registerFn = useCallback(
    (payload: any) => dispatch((register as any)(payload)),
    [dispatch]
  );
  const requestRegisterFn = useCallback(
    (payload: any) => dispatch((requestRegister as any)(payload)),
    [dispatch]
  );

  const initialEmail = localStorage.getItem(LocalStorageHelper.AUTH_EMAIL) || '';
  const [code, setCode] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resendCodeTimer, setResendCodeTimer] = useState(0);
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  const startResendCodeTimer = useCallback(() => {
    setResendCodeTimer(60);
    timerIntervalRef.current = setInterval(() => {
      setResendCodeTimer((prev) => {
        if (prev <= 1) {
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const { tanstackForm, handleSubmit, validateFormObj } = useRegisterForm({
    initialEmail,
    isCodeStep: code,
    onSubmit: (values, obj) => {
      setSubmitted(true);

      if (code) {
        registerFn({
          form: values,
          obj,
          success: (_response: unknown, _status: unknown, xhr: XMLHttpRequest) => {
            const header = xhr.getResponseHeader('Authorization');
            if (header) {
              localStorage.setItem(LocalStorageHelper.API_TOKEN, header);
              handleCloseModalFn();
              tanstackForm.reset();
              setSubmitted(false);
              setCode(false);
              setResendCodeTimer(0);
              if (typeof callback === 'function') {
                callback();
              }
            } else {
              NotifyManager.error('Ошибка', 'Ошибка авторизации');
            }
          },
          error: (_xhr: any, responseData?: any) => {
            setSubmitted(false);
            if (!responseData?.meta?.errors && responseData?.meta?.message) {
              NotifyManager.error('Ошибка', responseData.meta.message, 5000);
            }
          },
        });
      } else {
        requestRegisterFn({
          form: values,
          obj,
          success: () => {
            setCode(true);
            setSubmitted(false);
            startResendCodeTimer();
          },
          error: (_xhr: any, responseData?: any) => {
            setSubmitted(false);
            if (!responseData?.meta?.errors && responseData?.meta?.message) {
              NotifyManager.error('Ошибка', responseData.meta.message, 5000);
            }
          },
        });
      }
    },
  });

  const handleEnter = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.keyCode === 13) {
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const handleResendCode = useCallback(() => {
    if (resendCodeTimer > 0 || submitted) return;
    const values = tanstackForm.state.values;
    setSubmitted(true);
    requestRegisterFn({
      form: {
        email: values.email,
        password: values.password,
        password_confirmation: values.password_confirmation,
        promo_code: values.promo_code ?? '',
        role: values.role,
        form: values.form,
      },
      obj: validateFormObj,
      success: () => {
        setSubmitted(false);
        startResendCodeTimer();
      },
      error: (_xhr: any, responseData?: any) => {
        setSubmitted(false);
        if (!responseData?.meta?.errors && responseData?.meta?.message) {
          NotifyManager.error('Ошибка', responseData.meta.message, 5000);
        }
      },
    });
  }, [
    resendCodeTimer,
    submitted,
    tanstackForm,
    validateFormObj,
    requestRegisterFn,
    startResendCodeTimer,
  ]);

  if (code) {
    return (
      <Wrapper>
        <Container>
          <Text>
            {i18n.auth.codeAdvise} {tanstackForm.state.values.email}
          </Text>

          <tanstackForm.Field name="code">
            {(field) => (
              <Row>
                <InputContainer
                  error={
                    shouldShowFieldError(field.state.meta, tanstackForm.state)
                      ? getFieldErrorFromMeta(field.state.meta)
                      : undefined
                  }
                >
                  <Input
                    id="register-code"
                    type="text"
                    value={field.state.value}
                    onKeyDown={handleEnter}
                    onChange={(value) => field.handleChange(typeof value === 'string' ? value : '')}
                    onBlur={field.handleBlur}
                    placeholder={i18n.auth.code}
                  />
                </InputContainer>
              </Row>
            )}
          </tanstackForm.Field>

          {resendCodeTimer > 0 ? (
            <Row style={{ marginTop: '10px' }}>
              <TextTimer>
                {i18n.auth.resendCode} ({resendCodeTimer} секунд)
              </TextTimer>
            </Row>
          ) : (
            <Row style={{ marginTop: '10px', marginBottom: '0' }}>
              <Button
                wide
                id="register-resend-code"
                onClick={handleResendCode}
                disabled={submitted}
              >
                {i18n.auth.resendCodeButton}
              </Button>
            </Row>
          )}
          <GreyText>{i18n.auth.issue}</GreyText>
          <Row>
            <Button wide id="register-submit" onClick={handleSubmit} disabled={submitted}>
              {i18n.auth.confirm}
            </Button>
          </Row>
        </Container>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Container>
        <tanstackForm.Field name="email">
          {(field) => (
            <Row>
              <InputContainer
                error={
                  shouldShowFieldError(field.state.meta, tanstackForm.state)
                    ? getFieldErrorFromMeta(field.state.meta)
                    : undefined
                }
              >
                <Input
                  id="register-email"
                  type="text"
                  name="email"
                  autoComplete="on"
                  value={field.state.value}
                  onKeyDown={handleEnter}
                  onChange={(value) => field.handleChange(typeof value === 'string' ? value : '')}
                  onBlur={field.handleBlur}
                  placeholder={i18n.auth.email ?? 'Email'}
                />
              </InputContainer>
            </Row>
          )}
        </tanstackForm.Field>

        <tanstackForm.Field name="password">
          {(field) => (
            <Row>
              <InputContainer
                error={
                  shouldShowFieldError(field.state.meta, tanstackForm.state)
                    ? getFieldErrorFromMeta(field.state.meta)
                    : undefined
                }
              >
                <Input
                  id="register-password"
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  value={field.state.value}
                  onKeyDown={handleEnter}
                  onChange={(value) => field.handleChange(typeof value === 'string' ? value : '')}
                  onBlur={field.handleBlur}
                  placeholder={i18n.auth.password}
                />
              </InputContainer>
            </Row>
          )}
        </tanstackForm.Field>

        <tanstackForm.Field name="password_confirmation">
          {(field) => (
            <Row>
              <InputContainer
                error={
                  shouldShowFieldError(field.state.meta, tanstackForm.state)
                    ? getFieldErrorFromMeta(field.state.meta)
                    : undefined
                }
              >
                <Input
                  id="register-password_confirmation"
                  type="password"
                  name="password_confirmation"
                  autoComplete="new-password"
                  value={field.state.value}
                  onKeyDown={handleEnter}
                  onChange={(value) => field.handleChange(typeof value === 'string' ? value : '')}
                  onBlur={field.handleBlur}
                  placeholder={i18n.auth.passwordRepeat}
                />
              </InputContainer>
            </Row>
          )}
        </tanstackForm.Field>

        <tanstackForm.Field name="promo_code">
          {(field) => (
            <Row>
              <InputContainer
                error={
                  shouldShowFieldError(field.state.meta, tanstackForm.state)
                    ? getFieldErrorFromMeta(field.state.meta)
                    : undefined
                }
              >
                <Input
                  id="register-promo_code"
                  type="text"
                  name="promo_code"
                  value={field.state.value}
                  onKeyDown={handleEnter}
                  onChange={(value) => field.handleChange(typeof value === 'string' ? value : '')}
                  onBlur={field.handleBlur}
                  placeholder={i18n.auth.promoCode}
                />
              </InputContainer>
            </Row>
          )}
        </tanstackForm.Field>

        <Row style={{ margin: '10px 0' }}>
          <Caption>
            Регистрируясь на сайте Вы соглашаетесь с{' '}
            <a target="_blank" rel="noopener noreferrer" href="/agreement">
              Пользовательским соглашением
            </a>{' '}
            и{' '}
            <a target="_blank" rel="noopener noreferrer" href="/policy">
              Политикой по обработке персональных данных
            </a>
            .
          </Caption>
        </Row>
        <Row>
          <Button
            wide
            id="register-submit"
            onClick={handleSubmit}
            disabled={submitted || resendCodeTimer > 0}
          >
            {i18n.auth.register ?? i18n.auth.toRegister}
          </Button>
        </Row>
        {resendCodeTimer > 0 && (
          <Row style={{ marginTop: '10px' }}>
            <GreyText>Вы можете повторно отправить код через {resendCodeTimer} секунд.</GreyText>
          </Row>
        )}
      </Container>
    </Wrapper>
  );
}

export default Register;
